import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Factory, Truck, Users, Leaf, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const heroSlides = [
  {
    image: `${BACKEND_URL}/api/assets/images/site/hero-1.png`,
    key: 'heroSlide1',
  },
  {
    image: `${BACKEND_URL}/api/assets/images/site/hero-2.jpg`,
    key: 'heroSlide2',
  },
  {
    image: `${BACKEND_URL}/api/assets/images/site/hero-3.png`,
    key: 'heroSlide3',
  },
  {
    image: `${BACKEND_URL}/api/assets/images/site/hero-4.png`,
    key: 'heroSlide4',
  },
  {
    image: `${BACKEND_URL}/api/assets/images/site/hero-5.png`,
    key: 'heroSlide5',
  },
];

const stats = [
  { value: '30+', labelKey: 'yearsExperience' },
  { value: '2', labelKey: 'plants' },
  { value: '10', labelKey: 'productionLines' },
  { value: '400K', labelKey: 'annualCapacity' },
];

export const Home = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  return (
    <div className="bg-slate-50" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] overflow-hidden" data-testid="hero-section">
        {/* Slides */}
        {heroSlides.map((slide, index) => (
          <div
            key={slide.key}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}
            />
            <div className="absolute inset-0 bg-black/50" />
          </div>
        ))}

        {/* Content */}
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-3xl">
              <h1
                className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white uppercase tracking-tight mb-6 hero-slide-active"
                key={currentSlide}
                data-testid="hero-title"
              >
                {t(heroSlides[currentSlide].key)}
              </h1>
              <p className="text-lg sm:text-xl text-slate-200 mb-8 max-w-xl">
                {t('footerTagline')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/products">
                  <Button
                    className="bg-white hover:bg-slate-100 text-slate-900 rounded-none uppercase tracking-wider font-bold px-8 py-6 text-sm btn-industrial"
                    data-testid="hero-products-btn"
                  >
                    {t('ourProducts')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white rounded-none uppercase tracking-wider font-bold px-8 py-6 text-sm btn-industrial"
                    data-testid="hero-contact-btn"
                  >
                    {t('talkToSpecialist')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/30 hover:bg-black/50 text-white transition-colors"
          data-testid="hero-prev"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/30 hover:bg-black/50 text-white transition-colors"
          data-testid="hero-next"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-2">
          {heroSlides.map((_, index) => (
            <button
              key={`dot-${index}`}
              onClick={() => setCurrentSlide(index)}
              className={`w-12 h-1 transition-colors ${
                index === currentSlide ? 'bg-white' : 'bg-white/40'
              }`}
              data-testid={`hero-indicator-${index}`}
            />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div
                key={stat.labelKey}
                className="text-center stat-item"
                style={{ animationDelay: `${idx * 0.1}s` }}
                data-testid={`stat-${stat.labelKey}`}
              >
                <div className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-400 text-sm uppercase tracking-wider">
                  {t(stat.labelKey)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section className="py-20 md:py-32" data-testid="about-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 uppercase tracking-tight mb-6">
                {t('aboutTitle')}
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                {t('aboutDescription')}
              </p>
              <Link to="/about">
                <Button
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-none uppercase tracking-wider font-bold px-8 py-4 btn-industrial"
                  data-testid="about-cta"
                >
                  {t('aboutUs')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src={`${BACKEND_URL}/api/assets/images/site/hero-1.png`}
                alt="TRH Steel Facility"
                className="w-full h-auto shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-green-600 text-white p-6">
                <div className="font-heading text-3xl font-bold">1998</div>
                <div className="text-sm uppercase tracking-wider">{t('since')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-32 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 uppercase tracking-tight mb-4">
              {t('whyTrhSteel')}
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              {t('whyTrhSteelDesc')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Factory, title: t('reliability'), desc: t('reliabilityDesc') },
              { icon: Truck, title: t('consistency'), desc: t('consistencyDesc') },
              { icon: Users, title: t('flexibility'), desc: t('flexibilityDesc') },
              { icon: Leaf, title: t('longTermVision'), desc: t('longTermVisionDesc') },
            ].map((feature) => (
              <div
                key={feature.title}
                className="bg-slate-50 p-8 border border-slate-200 card-lift"
                data-testid={`feature-${feature.title}`}
              >
                <feature.icon className="h-10 w-10 text-slate-900 mb-4" />
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability Highlight */}
      <section className="py-20 md:py-32 bg-slate-900 text-white" data-testid="sustainability-highlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-green-500 text-sm uppercase tracking-wider font-bold mb-4 block">
                {t('sustainability')}
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight mb-6">
                {t('sustainabilitySubtitle')}
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                {t('sustainabilityHomeDesc')}
              </p>
              <Link to="/sustainability">
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white rounded-none uppercase tracking-wider font-bold px-8 py-4 btn-industrial"
                  data-testid="sustainability-cta"
                >
                  {t('sustainability')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800 p-6">
                <div className="font-heading text-4xl font-bold text-green-500 mb-2">30%</div>
                <div className="text-slate-400 text-sm uppercase tracking-wider">
                  {t('renewableEnergy')}
                </div>
              </div>
              <div className="bg-slate-800 p-6">
                <div className="font-heading text-4xl font-bold text-green-500 mb-2">ISO</div>
                <div className="text-slate-400 text-sm uppercase tracking-wider">
                  14001:2015
                </div>
              </div>
              <div className="bg-slate-800 p-6 col-span-2">
                <div className="font-heading text-2xl font-bold text-white mb-2">
                  {t('circularEconomy')}
                </div>
                <div className="text-slate-400 text-sm">
                  {t('circularEconomyDesc')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 md:py-32 bg-slate-100" data-testid="contact-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 uppercase tracking-tight mb-6">
            {t('readyToWork')}
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-8">
            {t('readyToWorkDesc')}
          </p>
          <Link to="/contact">
            <Button
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-none uppercase tracking-wider font-bold px-12 py-6 text-base btn-industrial"
              data-testid="contact-main-cta"
            >
              {t('talkToSpecialist')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
